import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Shell } from "@/components/Shell";
import { ContactPicker } from "@/components/ContactPicker";
import { ChipGroup, MultiChipGroup, Section } from "@/components/fields";
import { OrderEntry } from "@/components/OrderEntry";
import { shareOrderPdf } from "@/lib/pdf";
import { STOCKIST_PURPOSE, CONTACT_RESULT, FOLLOW_UP, STOCK_POSITION, COLLECTION_STATUS, SUPPORT_REQUIRED, PAYMENT_METHOD, label } from "@/constants";
import { saveVisit } from "@/lib/offline";
import { getGps } from "@/lib/format";
import { toast } from "sonner";
import { VisitTimePicker } from "@/components/VisitTimePicker";
import { VisitWarningDialog } from "@/components/VisitWarningDialog";
import { validateVisitTime } from "@/lib/visitTime";
import { WhatsappLogo, CaretLeft } from "@phosphor-icons/react";

const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + Number(n)); return d.toISOString().slice(0, 10); };

export default function StockistVisit() {
  const { user, session } = useAuth();
  const { lang, t } = useLang();
  const nav = useNavigate();
  const [areas, setAreas] = useState([]);
  const [products, setProducts] = useState([]);
  const [contact, setContact] = useState(null);
  const [result, setResult] = useState("contact_met");
  const [followUp, setFollowUp] = useState("none");
  const [purpose, setPurpose] = useState([]);
  const [stock, setStock] = useState({});
  const [items, setItems] = useState([]);
  const [collection, setCollection] = useState("none");
  const [amount, setAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [ref, setRef] = useState("");
  const [support, setSupport] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [visitTime, setVisitTime] = useState(() => new Date().toISOString());
  const [warnings, setWarnings] = useState([]);
  const [pendingNext, setPendingNext] = useState(false);
  const [todayVisits, setTodayVisits] = useState([]);
  const [maxBackdate, setMaxBackdate] = useState(720);

  useEffect(() => {
    api.get("/areas").then(({ data }) => setAreas(data));
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/visits", { params: { date: new Date().toISOString().slice(0, 10) } }).then(({ data }) => setTodayVisits(data));
    api.get("/settings").then(({ data }) => setMaxBackdate(data.max_backdate_minutes || 720));
  }, []);

  const reset = () => {
    setContact(null); setResult("contact_met"); setFollowUp("none");
    setPurpose([]); setStock({}); setItems([]); setCollection("none");
    setAmount(""); setRef(""); setSupport([]); setRemarks("");
    setVisitTime(new Date().toISOString()); setWarnings([]);
  };

  const doSave = async (next) => {
    setSaving(true);
    const gps = await getGps();
    const collected = ["full", "partial"].includes(collection);
    const payload = {
      id: crypto.randomUUID(), contact_type: "stockist", contact_id: contact.id,
      contact_name: contact.name || contact.business_name,
      working_area: session?.working_area || contact.area,
      visit_time: visitTime, created_at: new Date().toISOString(),
      call_status: result,
      purpose, stock_position: Object.entries(stock).map(([product, status]) => ({ product, status })),
      order_items: items, collection_status: collection,
      collection_amount: collected ? Number(amount || 0) : 0,
      payment_method: payMethod, reference_no: ref, support_required: support,
      follow_up_date: followUp !== "none" ? addDays(followUp) : null,
      remarks, gps,
    };
    const res = await saveVisit(payload);
    setSaving(false); setWarnings([]);
    toast.success(res.offline ? t("saved_offline") : t("save_visit"));
    if (next) reset(); else nav("/add-visit");
  };

  const save = (next) => {
    const v = validateVisitTime({ visitTime, sessionStart: session?.start_time,
      existingVisits: todayVisits, contactId: contact.id, maxBackdateMin: maxBackdate, t });
    if (v.block) { toast.error(v.block); return; }
    if (v.warnings.length) { setWarnings(v.warnings); setPendingNext(next); return; }
    doSave(next);
  };

  const showDetails = ["contact_met", "followup_required"].includes(result);

  if (!contact) {
    return (
      <Shell title={t("stockist_visit")} nav={false}
        back={<button onClick={() => nav("/add-visit")} data-testid="back-btn"><CaretLeft size={22} /></button>}>
        <ContactPicker ctype="stockist" areas={areas} onSelect={setContact} />
      </Shell>
    );
  }

  return (
    <Shell title={contact.name || contact.business_name} nav={false}
      back={<button onClick={() => setContact(null)} data-testid="back-btn"><CaretLeft size={22} /></button>}>
      <div className="space-y-4">
        <VisitTimePicker value={visitTime} onChange={setVisitTime} />

        <Section title={t("visit_result")} testid="section-result">
          <ChipGroup options={CONTACT_RESULT} value={result} onChange={setResult} testid="visit-result" />
        </Section>

        {showDetails && (
        <>
        <Section title={t("visit_purpose")} testid="section-purpose">
          <MultiChipGroup options={STOCKIST_PURPOSE} value={purpose} onChange={setPurpose} testid="visit-purpose" />
        </Section>

        <Section title={t("stock_position")} testid="section-stock">
          <div className="space-y-2">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-2 justify-between">
                <span className="text-sm text-slate-700 flex-1 truncate">{p.name}</span>
                <select
                  data-testid={`stock-${p.id}`}
                  value={stock[p.name] || "not_checked"}
                  onChange={(e) => setStock({ ...stock, [p.name]: e.target.value })}
                  className="min-h-[40px] rounded-lg border border-slate-200 px-2 text-sm bg-white"
                >
                  {STOCK_POSITION.map((a) => <option key={a.value} value={a.value}>{label(a, lang)}</option>)}
                </select>
              </div>
            ))}
          </div>
        </Section>

        <Section title={t("order_booking")} testid="section-order">
          <OrderEntry products={products} items={items} onChange={setItems} lang={lang} />
          {items.some((i) => (i.qty || 0) > 0) && (
            <button type="button" onClick={async () => { const r = await shareOrderPdf({ contactName: contact.name || contact.business_name, contactType: "Stockist", area: contact.area, mrName: user?.name, items }); if (r === "downloaded" || r === "shared") toast.success(t("share_order_pdf")); }} data-testid="share-order-pdf" className="mt-3 w-full min-h-[48px] rounded-lg border-2 border-emerald-500 text-emerald-600 font-semibold flex items-center justify-center gap-2">
              <WhatsappLogo size={18} weight="fill" /> {t("share_order_pdf")}
            </button>
          )}
        </Section>

        <Section title={t("collection_status")} testid="section-collection">
          <ChipGroup options={COLLECTION_STATUS} value={collection} onChange={setCollection} testid="collection-status" />
          {["full", "partial"].includes(collection) && (
            <div className="mt-3 space-y-3">
              <input data-testid="collection-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="numeric" placeholder={t("amount")} className="w-full min-h-[48px] rounded-lg border border-slate-200 px-3" />
              <ChipGroup options={PAYMENT_METHOD} value={payMethod} onChange={setPayMethod} testid="payment-method" />
              <input data-testid="reference-no" value={ref} onChange={(e) => setRef(e.target.value)} placeholder={t("reference_no")} className="w-full min-h-[48px] rounded-lg border border-slate-200 px-3" />
            </div>
          )}
        </Section>

        <Section title={t("support_required")} testid="section-support">
          <MultiChipGroup options={SUPPORT_REQUIRED} value={support} onChange={setSupport} testid="support-required" />
        </Section>

        <Section title={t("follow_up")} testid="section-followup">
          <ChipGroup options={FOLLOW_UP} value={followUp} onChange={setFollowUp} testid="follow-up" />
        </Section>
        </>
        )}

        <Section title={t("remarks")} testid="section-remarks">
          <textarea data-testid="remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className="w-full rounded-lg border border-slate-200 p-3" />
        </Section>

        <div className="flex gap-2">
          <button onClick={() => nav("/add-visit")} data-testid="cancel-btn" className="flex-1 min-h-[52px] rounded-lg border border-slate-300 font-semibold text-slate-600">{t("cancel")}</button>
          <button onClick={() => save(true)} disabled={saving} data-testid="save-next-btn" className="flex-1 min-h-[52px] rounded-lg bg-slate-800 text-white font-semibold">{t("save_next")}</button>
          <button onClick={() => save(false)} disabled={saving} data-testid="save-visit-btn" className="flex-1 min-h-[52px] rounded-lg bg-emerald-600 text-white font-semibold">{t("save_visit")}</button>
        </div>

        <VisitWarningDialog warnings={warnings} onCancel={() => setWarnings([])} onProceed={() => { setWarnings([]); doSave(pendingNext); }} />
      </div>
    </Shell>
  );
}
